import React, { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import DeviceProjectsTreePopup from '#component/device/dashboard/projects-tree';
import { changeOrderAndOrderBy } from '#action/device/devices-paginated';
import PreloaderComponent from '#component/preloader';
import NotFoundMessage from '#component/general/not-found-message';
import ConfigurableTable from '#component/table/configurable-table/configurable-table';
import { webPortalApi } from '#config/web-portal-api';
import { Paper } from './styles';
import { tableNameType } from '#type';
import { getRequestOptions } from '#helper';

const freezerURL = import.meta.env?.VITE_FREEZER_LINK_URL;
const freezerPrefix = import.meta.env?.VITE_FREEZER_PREFIX ?? 'fr-';

/**
 * Renders table with devices.
 * Dispatches sorting actions.
 * @returns {React.FC}
 */

const TableViewNew = ({ replaceHistoryParams }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    devices,
    status,
    order,
    orderBy
  } = useSelector(s => s.devicesPaginated, shallowEqual);
  const meta = useSelector(s => s.meta, shallowEqual);
  const account = useSelector(s => s.account, shallowEqual);
  const { device: deviceFilter } = useSelector(s => s.listFilter, shallowEqual);
  const theme = useTheme();
  const loading = status !== 'succeeded' || status === 'idle';

  const [showDetailsDialog, setDetailsDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState({});

  const handleRequestSort = useCallback((event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    replaceHistoryParams({ filterType: 'order', value: newOrder });
    replaceHistoryParams({ filterType: 'orderBy', value: property });
    dispatch(changeOrderAndOrderBy(newOrder, property));
  }, [dispatch, order, orderBy, replaceHistoryParams]);

  const generateOtc = async () => {
    const response = await fetch(
      webPortalApi.buildApiUrl('user/application-code'),
      {
        ...getRequestOptions(),
        method: 'GET'
      }
    );
    const data = await response.json();
    return data.code;
  };

  const handleTableRowClick = useCallback(device => async e => {
    if (loading) {
      return;
    }

    const isFreezer = device.type === 'freezer';
    let url = '';

    if (isFreezer) {
      if (!freezerURL) {
        console.error('Freezer URL not configured');
        return;
      }

      const otc = await generateOtc();
      const freezerPrefixRegex = new RegExp(freezerPrefix, 'i');
      url = `${freezerURL}/freezers/freezer/${device.uid}/${device.title.replace(freezerPrefixRegex, '')}/?code=${otc}`;
    } else {
      url = `/device/${device.id}`;
    }

    const isNewWindow = isFreezer || (e.ctrlKey && e.type === 'click');

    if (isNewWindow) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  }, [history, loading]);

  const handleTableRowMouseDown = useCallback(device => e => {
    if (e.nativeEvent.which === 2) {
      window.open(`/device/${device.projects[0]}/${device.id}`, '_blank');
    }
  }, []);

  const tableHeadProps = useMemo(() => ({
    account,
    order,
    orderBy,
    handleRequestSort
  }), [handleRequestSort, account, order, orderBy]);

  const tableBodyProps = useMemo(() => ({
    meta,
    account,
    loading,
    theme,
    setDetailsDialog,
    setSelectedDevice,
    onClick: handleTableRowClick,
    onMouseDown: handleTableRowMouseDown
  }), [
    account,
    setDetailsDialog,
    setSelectedDevice,
    handleTableRowClick,
    handleTableRowMouseDown,
    loading,
    meta,
    theme
  ]);

  if (!devices.length && loading) {
    return <PreloaderComponent />;
  }

  if (deviceFilter && !loading && !devices.length) {
    return (
      <NotFoundMessage>
        No devices
      </NotFoundMessage>
    );
  }

  return (
    <Paper className="default-table">
      <ConfigurableTable
        data={devices}
        headCellProps={tableHeadProps}
        bodyCellProps={tableBodyProps}
        tableName={tableNameType.DEVICE_TABLE}
      />
      {showDetailsDialog && <DeviceProjectsTreePopup
        open={showDetailsDialog}
        setOpen={setDetailsDialog}
        deviceUid={selectedDevice.uid}
        deviceTitle={selectedDevice.title}
        device={selectedDevice}
        setSelectedDevice={setSelectedDevice}
      />}
    </Paper>
  );
};

export default TableViewNew;
